import React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import CategoryIcon from '@mui/icons-material/Category';
import DescriptionIcon from '@mui/icons-material/Description';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import PeopleIcon from '@mui/icons-material/People';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ArchiveIcon from '@mui/icons-material/Archive';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import BackupIcon from '@mui/icons-material/Backup';
import BugReportIcon from '@mui/icons-material/BugReport';
import BarChartIcon from '@mui/icons-material/BarChart';
import ImageIcon from '@mui/icons-material/Image';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import TerminalIcon from '@mui/icons-material/Terminal';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PaletteIcon from '@mui/icons-material/Palette';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { IUser } from '../../types/damTypes';
import { changePassword } from '../../services/authService';
import { hasAnyPermission, hasPermission, canAccessProductComparison } from '../../utils/permissionUtils';

type SettingsView =
  | 'roles'
  | 'users'
  | 'apikeys'
  | 'backups'
  | 'inventory'
  | 'csv'
  | 'field-diagnostics'
  | 'favicon'
  | 'archive'
  | 'duplicates'
  | 'download-templates'
  | 'thumbnails'
  | 'categories'
  | 'login-page'
  | 'ai-queue'
  | 'popup'
  | 'brand-guide'
  | 'analytics'
  | 'maintenance'
  | 'imagemagick-test'
  | 'product-comparison';

export default function MoreScreen(props: {
  currentUser: IUser;
  onLogout: (options?: { reason?: string; server?: boolean }) => void;
  onOpenSettings: (view: SettingsView) => void;
  onOpenTemplates: () => void;
}) {
  const { currentUser, onLogout, onOpenSettings, onOpenTemplates } = props;

  const [openChangePassword, setOpenChangePassword] = React.useState(false);
  const [cpCurrent, setCpCurrent] = React.useState('');
  const [cpNew, setCpNew] = React.useState('');
  const [cpConfirm, setCpConfirm] = React.useState('');
  const [cpError, setCpError] = React.useState<string | null>(null);
  const [cpLoading, setCpLoading] = React.useState(false);

  const canAdminAll = hasPermission(currentUser, 'admin:all');

  const showArchive = hasPermission(currentUser, 'assets:archive');
  const showCategories = hasPermission(currentUser, 'categories:read');
  const showTemplates = hasPermission(currentUser, 'templates:read');
  const showDownloadTemplates = hasPermission(currentUser, 'downloadTemplates:read');
  const showProductComparison = canAccessProductComparison(currentUser);
  const showCsv = hasAnyPermission(currentUser, ['csv:import', 'csv:export', 'csv:manageTemplates']);
  const showApiKeys = hasPermission(currentUser, 'apikeys:read');
  const showUsers = hasPermission(currentUser, 'users:read');
  const showRoles = hasPermission(currentUser, 'roles:read');
  const showMaintenance = hasPermission(currentUser, 'system:manageMaintenance');

  const handleConfirmChangePassword = async () => {
    try {
      setCpError(null);
      if (!cpCurrent || !cpNew) {
        setCpError('Fyll i alla fält.');
        return;
      }
      if (cpNew !== cpConfirm) {
        setCpError('Det nya lösenordet och bekräftelsen matchar inte.');
        return;
      }
      setCpLoading(true);
      const resp = await changePassword(cpCurrent, cpNew);
      if (resp.error) {
        setCpError(resp.error || 'Kunde inte byta lösenord.');
      } else {
        setOpenChangePassword(false);
        if (resp.data?.logoutRequired) {
          onLogout({ reason: 'password_changed', server: false });
        }
      }
    } catch (err: any) {
      setCpError(err?.message || 'Ett fel inträffade.');
    } finally {
      setCpLoading(false);
    }
  };

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', bgcolor: '#f5f5f5' }}>
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Mer
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap>
          {currentUser.email}
        </Typography>
      </Box>

      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
          Konto
        </Typography>
        <List sx={{ bgcolor: 'background.paper', borderRadius: 2, overflow: 'hidden' }} disablePadding>
          <ListItemButton
            onClick={() => {
              setCpCurrent('');
              setCpNew('');
              setCpConfirm('');
              setCpError(null);
              setOpenChangePassword(true);
            }}
          >
            <ListItemIcon><LockIcon /></ListItemIcon>
            <ListItemText primary="Byt lösenord" />
          </ListItemButton>
          <Divider />
          <ListItemButton onClick={() => onLogout()}>
            <ListItemIcon><LogoutIcon color="error" /></ListItemIcon>
            <ListItemText primary="Logga ut" />
          </ListItemButton>
        </List>

        {(showArchive || showCategories || showTemplates || showDownloadTemplates || showProductComparison || showCsv || showApiKeys || showUsers || showRoles || showMaintenance || canAdminAll) && (
          <>
            <Typography variant="subtitle2" sx={{ color: 'text.secondary', mt: 3, mb: 1 }}>
              Admin / Inställningar
            </Typography>
            <List sx={{ bgcolor: 'background.paper', borderRadius: 2, overflow: 'hidden' }} disablePadding>
              {showArchive && (
                <>
                  <ListItemButton onClick={() => onOpenSettings('archive')}>
                    <ListItemIcon><ArchiveIcon /></ListItemIcon>
                    <ListItemText primary="Arkiv" />
                  </ListItemButton>
                  <Divider />
                </>
              )}

              {showCategories && (
                <>
                  <ListItemButton onClick={() => onOpenSettings('categories')}>
                    <ListItemIcon><CategoryIcon /></ListItemIcon>
                    <ListItemText primary="Kategorier" />
                  </ListItemButton>
                  <Divider />
                </>
              )}

              {showTemplates && (
                <>
                  <ListItemButton onClick={onOpenTemplates}>
                    <ListItemIcon><DescriptionIcon /></ListItemIcon>
                    <ListItemText primary="Mallar (Templates)" />
                  </ListItemButton>
                  <Divider />
                </>
              )}

              {showDownloadTemplates && (
                <>
                  <ListItemButton onClick={() => onOpenSettings('download-templates')}>
                    <ListItemIcon><DescriptionIcon /></ListItemIcon>
                    <ListItemText primary="Nedladdningsmall Bilder" />
                  </ListItemButton>
                  <Divider />
                </>
              )}

              {showProductComparison && (
                <>
                  <ListItemButton onClick={() => onOpenSettings('product-comparison')}>
                    <ListItemIcon><CompareArrowsIcon /></ListItemIcon>
                    <ListItemText primary="Produktkontroll" />
                  </ListItemButton>
                  <Divider />
                </>
              )}

              {showCsv && (
                <>
                  <ListItemButton onClick={() => onOpenSettings('csv')}>
                    <ListItemIcon><ImportExportIcon /></ListItemIcon>
                    <ListItemText primary="CSV Import/Export" />
                  </ListItemButton>
                  <Divider />
                </>
              )}

              {showApiKeys && (
                <>
                  <ListItemButton onClick={() => onOpenSettings('apikeys')}>
                    <ListItemIcon><VpnKeyIcon /></ListItemIcon>
                    <ListItemText primary="API-nycklar" />
                  </ListItemButton>
                  <Divider />
                </>
              )}

              {showUsers && (
                <>
                  <ListItemButton onClick={() => onOpenSettings('users')}>
                    <ListItemIcon><PeopleIcon /></ListItemIcon>
                    <ListItemText primary="Användare" />
                  </ListItemButton>
                  <Divider />
                </>
              )}

              {showRoles && (
                <>
                  <ListItemButton onClick={() => onOpenSettings('roles')}>
                    <ListItemIcon><AdminPanelSettingsIcon /></ListItemIcon>
                    <ListItemText primary="Roller" />
                  </ListItemButton>
                  <Divider />
                </>
              )}

              {showMaintenance && (
                <>
                  <ListItemButton onClick={() => onOpenSettings('maintenance')}>
                    <ListItemIcon><AnnouncementIcon /></ListItemIcon>
                    <ListItemText primary="Underhåll" />
                  </ListItemButton>
                  <Divider />
                </>
              )}

              {canAdminAll && (
                <>
                  <ListItemButton onClick={() => onOpenSettings('inventory')}>
                    <ListItemIcon><Inventory2OutlinedIcon /></ListItemIcon>
                    <ListItemText primary="Lager & Order" />
                  </ListItemButton>
                  <Divider />

                  <ListItemButton onClick={() => onOpenSettings('analytics')}>
                    <ListItemIcon><BarChartIcon /></ListItemIcon>
                    <ListItemText primary="Analytics" />
                  </ListItemButton>
                  <Divider />

                  <ListItemButton onClick={() => onOpenSettings('backups')}>
                    <ListItemIcon><BackupIcon /></ListItemIcon>
                    <ListItemText primary="Backuper" />
                  </ListItemButton>
                  <Divider />

                  <ListItemButton onClick={() => onOpenSettings('duplicates')}>
                    <ListItemIcon><SettingsIcon /></ListItemIcon>
                    <ListItemText primary="Dublettkontroll" />
                  </ListItemButton>
                  <Divider />

                  <ListItemButton onClick={() => onOpenSettings('field-diagnostics')}>
                    <ListItemIcon><BugReportIcon /></ListItemIcon>
                    <ListItemText primary="Fältdiagnostik" />
                  </ListItemButton>
                  <Divider />

                  <ListItemButton onClick={() => onOpenSettings('favicon')}>
                    <ListItemIcon><ImageIcon /></ListItemIcon>
                    <ListItemText primary="Favicon" />
                  </ListItemButton>
                  <Divider />

                  <ListItemButton onClick={() => onOpenSettings('login-page')}>
                    <ListItemIcon><PaletteIcon /></ListItemIcon>
                    <ListItemText primary="Inloggningssida" />
                  </ListItemButton>
                  <Divider />

                  <ListItemButton onClick={() => onOpenSettings('ai-queue')}>
                    <ListItemIcon><SmartToyIcon /></ListItemIcon>
                    <ListItemText primary="AI‑kö" />
                  </ListItemButton>
                  <Divider />

                  <ListItemButton onClick={() => onOpenSettings('popup')}>
                    <ListItemIcon><AnnouncementIcon /></ListItemIcon>
                    <ListItemText primary="Popup-nyheter" />
                  </ListItemButton>
                  <Divider />

                  <ListItemButton onClick={() => onOpenSettings('imagemagick-test')}>
                    <ListItemIcon><TerminalIcon /></ListItemIcon>
                    <ListItemText primary="ImageMagick Test" />
                  </ListItemButton>
                </>
              )}
            </List>
          </>
        )}
      </Box>

      {/* Dialog: Byt lösenord */}
      <Dialog open={openChangePassword} onClose={() => setOpenChangePassword(false)} fullWidth maxWidth="sm">
        <DialogTitle>Byt lösenord</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Ange ditt nuvarande lösenord och ett nytt lösenord.
          </Typography>
          <TextField
            margin="normal"
            fullWidth
            label="Nuvarande lösenord"
            type="password"
            value={cpCurrent}
            onChange={(e) => setCpCurrent(e.target.value)}
            disabled={cpLoading}
          />
          <TextField
            margin="normal"
            fullWidth
            label="Nytt lösenord"
            type="password"
            value={cpNew}
            onChange={(e) => setCpNew(e.target.value)}
            disabled={cpLoading}
          />
          <TextField
            margin="normal"
            fullWidth
            label="Bekräfta nytt lösenord"
            type="password"
            value={cpConfirm}
            onChange={(e) => setCpConfirm(e.target.value)}
            disabled={cpLoading}
          />
          {cpError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {cpError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenChangePassword(false)} disabled={cpLoading}>Avbryt</Button>
          <Button variant="contained" onClick={handleConfirmChangePassword} disabled={cpLoading}>
            {cpLoading ? 'Byter…' : 'Byt lösenord'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

